import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import axios from 'axios';
import { getEnvVar } from '../utils/getEnvVar';
import MovdButton from '../design-system/MovdButton.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const stripeKey = getEnvVar('VITE_STRIPE_PUBLISHABLE_KEY', '');
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

function PaymentForm({ price, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const submit = async event => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');
    const result = await stripe.confirmPayment({ elements, confirmParams: {}, redirect: 'if_required' });
    if (result.error) setError(result.error.message || 'Payment could not be completed.');
    else if (result.paymentIntent?.status === 'succeeded') {
      setSubmitted(true);
      onSuccess?.(result.paymentIntent);
    }
    setLoading(false);
  };

  if (submitted) return <div role="status" style={{ padding: 24, borderTop: `3px solid ${movdTokens.color.teal}`, background: movdTokens.color.paper }}><div style={{ color: '#176B60', fontSize: 12, fontWeight: 900, letterSpacing: '.14em' }}>PAYMENT RECEIVED</div><h2 style={{ margin: '8px 0', color: movdTokens.color.ink }}>Confirming your move</h2><p style={{ color: '#53636A', margin: 0 }}>MOVD is waiting for the booking confirmation from the server.</p></div>;

  return <form onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
    <div style={{ padding: 18, background: movdTokens.color.paper, border: `1px solid ${movdTokens.color.paperMuted}`, borderRadius: 10 }}><PaymentElement options={{ layout: 'tabs' }} /></div>
    <MovdButton type="submit" loading={loading} disabled={!stripe || !elements} style={{ width: '100%', minHeight: 56 }}>Confirm & pay ${Number(price).toLocaleString('en-CA')}</MovdButton>
    {error && <div role="alert" style={{ color: '#9F1239', background: '#FFF1F2', border: '1px solid #FDA4AF', padding: 12, borderRadius: 8 }}>{error}</div>}
    <p style={{ color: '#53636A', fontSize: 12, textAlign: 'center', margin: 0 }}>Secure payment powered by Stripe. Your booking is confirmed after payment verification.</p>
  </form>;
}

export default function EscrowStripePayment({ price, userEmail, moverName, bookingId, jobType = 'local', premiumProtection = false, onSuccess }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.post('/api/escrow/create-escrow-payment', { price, userEmail, moverName, bookingId, jobType, premiumProtection }, { timeout: 20000 });
        if (active) setClientSecret(response.data.clientSecret);
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.error || 'Failed to prepare secure payment.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [bookingId, jobType, moverName, premiumProtection, price, userEmail]);

  if (loading) return <div role="status" style={{ padding: 24, textAlign: 'center', color: '#53636A' }}>Preparing secure payment…</div>;
  if (error) return <div role="alert" style={{ color: '#9F1239', background: '#FFF1F2', border: '1px solid #FDA4AF', padding: 14, borderRadius: 8 }}>{error}</div>;
  if (!clientSecret) return null;
  return <Elements stripe={stripePromise} options={{ clientSecret }}><PaymentForm price={price} onSuccess={onSuccess} /></Elements>;
}
